import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Network, Scales, MapPin, Star, ShieldCheck, ArrowRight, Globe, Users } from "@phosphor-icons/react";

const TABS = [
  { id: "graph",  label: "Counterfeiter Graph",  icon: Network },
  { id: "counsel", label: "Counsel Network",     icon: Scales  },
  { id: "field",   label: "Field Marketplace",   icon: MapPin  },
];

export default function NetworkPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("graph");
  const [optedIn, setOptedIn] = useState(false);
  const [clusters, setClusters] = useState({ items: [], clusters: [] });
  const [counsel, setCounsel] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [filters, setFilters] = useState({ country: "", specialty: "", city: "" });
  const [intros, setIntros] = useState([]);

  // graph load
  const loadGraph = async () => {
    try {
      const r = await api.get("/graph/clusters", { params: filters.country ? { country: filters.country } : {} });
      setClusters(r.data);
      setOptedIn(true);
    } catch (e) {
      if (e.response?.status === 403) setOptedIn(false);
    }
  };
  const optIn = async () => {
    await api.post("/graph/opt-in");
    toast.success("Opted in to the SPAN Counterfeiter Graph");
    loadGraph();
  };
  const loadCounsel = async () => {
    const r = await api.get("/counsel", { params: filters });
    setCounsel(r.data);
  };
  const loadOfficers = async () => {
    const r = await api.get("/field-officers", { params: filters });
    setOfficers(r.data);
  };
  const requestIntro = async (c) => {
    try {
      const r = await api.post("/counsel/introduce", { counsel_id: c.counsel_id, note: "Inbound intro from network directory" });
      setIntros((prev) => [r.data, ...prev]);
      toast.success(`Intro requested with ${c.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Intro failed");
    }
  };

  useEffect(() => {
    if (tab === "graph")   loadGraph();
    if (tab === "counsel") loadCounsel();
    if (tab === "field")   loadOfficers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filters]);

  return (
    <div className="space-y-6" data-testid="network-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">SPAN NETWORK</div>
          <h1 className="font-display font-bold text-[28px] tracking-tight mt-1">The brand-protection commons.</h1>
          <p className="text-[13px] text-[var(--span-text-2)] mt-1">Shared intel · verified counsel · vetted field officers — across the network.</p>
        </div>
        <div className="flex gap-1 border border-[var(--span-border)]">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`network-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={"px-4 py-2 text-[12px] font-medium flex items-center gap-2 " + (tab === t.id ? "bg-[#0A0B0D] text-white" : "hover:bg-[var(--span-bg)]")}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ----- COUNTERFEITER GRAPH ----- */}
      {tab === "graph" && (
        <div className="space-y-4">
          {!optedIn ? (
            <div className="card-flat p-8 lg:p-12 text-center" data-testid="graph-optin">
              <Globe size={36} weight="duotone" className="mx-auto" />
              <div className="font-display font-bold text-[22px] mt-4">Join the SPAN Counterfeiter Graph</div>
              <p className="text-[14px] text-[var(--span-text-2)] max-w-[620px] mx-auto mt-3 leading-[1.55]">
                Opt in to share anonymized intel — when one SPAN customer flags a counterfeiter cluster,
                every other opt-in member sees it instantly. No brand names are shared, only locations,
                signal types, and severity. The same counterfeiter targets multiple brands —
                make them <i>discoverable to all of us</i>.
              </p>
              <button data-testid="graph-optin-btn" onClick={optIn} className="span-btn mt-6">
                Opt my organization in <ArrowRight size={13} />
              </button>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--span-text-2)] mt-4">
                Anonymized · Reciprocal · Cancel anytime
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 text-[12px] text-[var(--span-text-2)]">
                <Users size={14} /> <span data-testid="graph-status">{clusters.total} anonymized signals across {clusters.clusters?.length} hotspots</span>
                <select
                  data-testid="graph-country-filter"
                  className="ml-auto border border-[var(--span-border)] text-[12px] px-2 py-1 bg-[var(--span-surface)]"
                  value={filters.country}
                  onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
                >
                  <option value="">All countries</option>
                  <option value="IN">India</option>
                  <option value="BD">Bangladesh</option>
                  <option value="AE">UAE</option>
                  <option value="SG">Singapore</option>
                  <option value="NP">Nepal</option>
                </select>
              </div>

              <div className="grid lg:grid-cols-2 gap-3">
                {/* Clusters */}
                <div className="card-flat p-5">
                  <div className="label-eyebrow mb-3">Hotspot clusters</div>
                  <div className="space-y-2">
                    {clusters.clusters?.slice(0, 10).map((c, i) => (
                      <div key={i} className="flex items-center justify-between border-b border-[var(--span-border)] py-2 last:border-0">
                        <div>
                          <div className="font-display font-medium text-[14px]">{c.location}</div>
                          <div className="text-[11px] font-mono text-[var(--span-text-2)]">{c.types.join(" · ")}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-display font-bold text-[18px] tnum">{c.count}</div>
                          <div className={"text-[10px] font-mono uppercase " + (c.severity_max === "critical" ? "text-red-600" : c.severity_max === "high" ? "text-amber-600" : "text-[var(--span-text-2)]")}>{c.severity_max}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Raw items */}
                <div className="card-flat p-5">
                  <div className="label-eyebrow mb-3">Recent shared signals</div>
                  <div className="space-y-3 max-h-[420px] overflow-auto pr-2">
                    {clusters.items?.map((s) => (
                      <div key={s.shared_id} className="border-l-2 border-[var(--span-border-strong)] pl-3 py-1">
                        <div className="flex items-center gap-2 text-[10px] font-mono text-[var(--span-text-2)]">
                          <span>{s.signal_type}</span>
                          <span>·</span>
                          <span className={s.severity === "critical" ? "text-red-600" : s.severity === "high" ? "text-amber-600" : ""}>{s.severity}</span>
                          <span>·</span>
                          <span>{s.location}</span>
                        </div>
                        <div className="text-[12px] mt-1">{s.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ----- COUNSEL NETWORK ----- */}
      {tab === "counsel" && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <select
              data-testid="counsel-country-filter"
              className="border border-[var(--span-border)] text-[12px] px-2 py-1 bg-[var(--span-surface)]"
              value={filters.country}
              onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
            >
              <option value="">All countries</option>
              <option value="IN">India</option><option value="BD">Bangladesh</option>
              <option value="AE">UAE</option><option value="SG">Singapore</option>
              <option value="NP">Nepal</option>
            </select>
            <select
              data-testid="counsel-specialty-filter"
              className="border border-[var(--span-border)] text-[12px] px-2 py-1 bg-[var(--span-surface)]"
              value={filters.specialty}
              onChange={(e) => setFilters((f) => ({ ...f, specialty: e.target.value }))}
            >
              <option value="">All specialties</option>
              <option value="ip_litigation">IP litigation</option>
              <option value="trademark">Trademark</option>
              <option value="anti_counterfeiting">Anti-counterfeiting</option>
              <option value="copyright">Copyright</option>
              <option value="customs">Customs</option>
              <option value="cross_border">Cross-border</option>
            </select>
            <div className="ml-auto text-[11px] font-mono text-[var(--span-text-2)]">SPAN takes 10% intro fee on engaged matters</div>
          </div>
          <div className="grid lg:grid-cols-2 gap-3">
            {counsel.map((c) => (
              <div key={c.counsel_id} className="card-flat p-5" data-testid={`counsel-card-${c.counsel_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-display font-bold text-[16px]">{c.name}</div>
                      {c.verified && <ShieldCheck size={14} weight="fill" className="text-emerald-600" />}
                    </div>
                    <div className="text-[12px] text-[var(--span-text-2)]">{c.firm} · {c.city}, {c.country_code}</div>
                  </div>
                  <div className="flex items-center gap-1 text-[12px] font-mono">
                    <Star size={12} weight="fill" className="text-amber-500" /> {c.rating?.toFixed(1)}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-3">
                  {c.specialty?.map((s) => (
                    <span key={s} className="text-[10px] font-mono px-2 py-0.5 border border-[var(--span-border)] uppercase">{s.replace(/_/g," ")}</span>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-[var(--span-border)] text-[11px]">
                  <div><div className="font-mono text-[9px] text-[var(--span-text-2)]">RESPONSE</div><div className="tnum">{c.response_time_hrs}h</div></div>
                  <div><div className="font-mono text-[9px] text-[var(--span-text-2)]">FEE BAND</div><div className="capitalize">{c.fee_band}</div></div>
                  <div><div className="font-mono text-[9px] text-[var(--span-text-2)]">INTROS</div><div className="tnum">{c.intros_completed}</div></div>
                </div>
                <button
                  onClick={() => requestIntro(c)}
                  className="span-btn span-btn-ghost mt-4 w-full justify-center"
                  data-testid={`counsel-intro-${c.counsel_id}`}
                >
                  Request intro
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ----- FIELD OFFICER MARKETPLACE ----- */}
      {tab === "field" && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <select
              data-testid="field-country-filter"
              className="border border-[var(--span-border)] text-[12px] px-2 py-1 bg-[var(--span-surface)]"
              value={filters.country}
              onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
            >
              <option value="">All countries</option>
              <option value="IN">India</option><option value="BD">Bangladesh</option>
              <option value="AE">UAE</option><option value="NP">Nepal</option>
            </select>
            <select
              data-testid="field-specialty-filter"
              className="border border-[var(--span-border)] text-[12px] px-2 py-1 bg-[var(--span-surface)]"
              value={filters.specialty}
              onChange={(e) => setFilters((f) => ({ ...f, specialty: e.target.value }))}
            >
              <option value="">All specialties</option>
              <option value="surveillance">Surveillance</option>
              <option value="test_purchase">Test purchase</option>
              <option value="market_sweep">Market sweep</option>
              <option value="raid_support">Raid support</option>
              <option value="border_seizure">Border seizure</option>
              <option value="customs_liaison">Customs liaison</option>
            </select>
            <div className="ml-auto text-[11px] font-mono text-[var(--span-text-2)]">Escrow held by SPAN until evidence verified</div>
          </div>
          <div className="grid lg:grid-cols-2 gap-3">
            {officers.map((o) => (
              <div key={o.officer_id} className="card-flat p-5" data-testid={`officer-card-${o.officer_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-display font-bold text-[16px]">{o.name}</div>
                      {o.photo_kyc_verified && <ShieldCheck size={14} weight="fill" className="text-emerald-600" />}
                    </div>
                    <div className="text-[12px] text-[var(--span-text-2)]">{o.city}, {o.country_code}</div>
                  </div>
                  <div className="flex items-center gap-1 text-[12px] font-mono">
                    <Star size={12} weight="fill" className="text-amber-500" /> {o.rating?.toFixed(1)}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-3">
                  {o.specialty?.map((s) => (
                    <span key={s} className="text-[10px] font-mono px-2 py-0.5 border border-[var(--span-border)] uppercase">{s.replace(/_/g," ")}</span>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-[var(--span-border)] text-[11px]">
                  <div><div className="font-mono text-[9px] text-[var(--span-text-2)]">HOURLY</div><div className="tnum">₹{o.hourly_inr?.toLocaleString("en-IN")}</div></div>
                  <div><div className="font-mono text-[9px] text-[var(--span-text-2)]">DISPATCHES</div><div className="tnum">{o.completed_dispatches}</div></div>
                  <div><div className="font-mono text-[9px] text-[var(--span-text-2)]">ESCROW</div><div>{o.escrow_supported ? "Yes" : "No"}</div></div>
                </div>
                <div className="text-[10px] font-mono text-[var(--span-text-2)] mt-3">Dispatch from a case · open case → "Dispatch Field Officer"</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
